import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import { Icon } from '../icons/SpriteIcons';
import { formatIndianDate } from '../../utils/dateFormat';
import { formatTime12 } from '../../utils/eventDates';

/** The minimal event shape ShareButton needs - works with EventItem or any slice of it. */
export interface ShareableEvent {
  slug: string;
  title: string;
  date: string;
  time?: string;
  venue: string;
  city: string;
}

interface ShareButtonProps {
  event: ShareableEvent;
  /** 'icon' - round button for posters/cards. 'pill' - labelled button for detail pages / CTA rows. */
  variant?: 'icon' | 'pill';
  className?: string;
}

/** Canonical, shareable URL for an event - this is what opens the event back up on the platform. */
export const buildEventShareUrl = (slug: string): string => `${window.location.origin}/events/${slug}`;

const buildShareText = (event: ShareableEvent): string => {
  const when = [formatIndianDate(event.date), formatTime12(event.time)].filter(Boolean).join(' · ');
  const where = [event.venue, event.city].filter(Boolean).join(', ');
  const details = [when, where].filter(Boolean).join(' — ');
  return [event.title, details].filter(Boolean).join(' — ');
};

/**
 * Share control for an event: uses the device's native share sheet where available
 * (covers WhatsApp, Messages, Telegram, Mail, etc. on mobile in one tap), and falls
 * back to a small menu of direct options everywhere else. Always shares the event's
 * canonical `/events/:slug` URL, so the link opens straight back into that event here.
 *
 * Safe to nest inside a card that is itself a <Link> - the trigger stops the click
 * from bubbling (and is stacked above any full-card ::after link overlays) so it
 * never triggers navigation on the parent.
 */
export const ShareButton: React.FC<ShareButtonProps> = ({ event, variant = 'icon', className = '' }) => {
  const [open, setOpen] = useState(false);
  // Menu is portalled to <body> (see render below) because every place this button lives -
  // poster corners, the event-detail header - sits inside an `overflow-hidden` card, which
  // would otherwise clip the dropdown invisible. Position is measured from the trigger each
  // time it opens, so `menuPos` is what the portal is actually placed at.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const isOutside = (target: Node) =>
      !triggerRef.current?.contains(target) && !menuRef.current?.contains(target);
    const onDoc = (e: MouseEvent) => {
      if (isOutside(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // The menu's fixed position is only measured on open - rather than track every scroll/resize,
    // just close it, matching how the header's own dropdowns behave.
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const url = buildEventShareUrl(event.slug);
  const text = buildShareText(event);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy the link');
    }
    setOpen(false);
  };

  const openIntent = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const handleTrigger = async (e: React.MouseEvent) => {
    // The trigger can sit inside a <Link> (event cards) - never let the click fall through to it.
    e.preventDefault();
    e.stopPropagation();

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: event.title, text, url });
      } catch (err) {
        // AbortError just means the person dismissed the native sheet; anything else, show the fallback menu.
        if ((err as Error | null)?.name !== 'AbortError') openMenu();
      }
      return;
    }
    if (open) setOpen(false);
    else openMenu();
  };

  const menuItems: Array<{ key: string; label: string; icon?: string; glyph?: string; tint: string; action: () => void }> = [
    { key: 'copy', label: 'Copy link', icon: 'i-link', tint: 'text-cyan-300 bg-cyan-400/10 border-cyan-400/25', action: copyLink },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: 'i-chat',
      tint: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/25',
      action: () => openIntent(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`),
    },
    {
      key: 'x',
      label: 'X (Twitter)',
      glyph: 'X',
      tint: 'text-white bg-white/10 border-white/20',
      action: () => openIntent(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`),
    },
    {
      key: 'facebook',
      label: 'Facebook',
      glyph: 'f',
      tint: 'text-blue-300 bg-blue-400/10 border-blue-400/25',
      action: () => openIntent(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`),
    },
    {
      key: 'email',
      label: 'Email',
      icon: 'i-mail',
      tint: 'text-rose-300 bg-rose-400/10 border-rose-400/25',
      action: () =>
        openIntent(`mailto:?subject=${encodeURIComponent(event.title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`),
    },
  ];

  const triggerClass =
    variant === 'icon'
      ? `inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/55 text-white/80 backdrop-blur-md transition-colors hover:border-primary/40 hover:bg-black/70 hover:text-white ${className}`
      : `inline-flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-medium uppercase tracking-widest text-white/70 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white ${className}`;

  return (
    <div className="relative inline-block" style={{ zIndex: 20 }}>
      <button
        type="button"
        ref={triggerRef}
        onClick={handleTrigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Share ${event.title}`}
        className={triggerClass}
      >
        <Icon name="i-share" size={variant === 'icon' ? 16 : 14} />
        {variant === 'pill' ? <span>Share</span> : null}
      </button>
      {open && menuPos
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              onClick={(e) => e.stopPropagation()}
              style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
              className="z-[9999] w-56 overflow-hidden rounded-2xl border border-white/15 bg-[#14151d] p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.75)]"
            >
              {menuItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    item.action();
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-[10px] border text-xs font-bold ${item.tint}`}
                  >
                    {item.icon ? <Icon name={item.icon} size={15} /> : item.glyph}
                  </span>
                  {item.label}
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};
